import React, { useMemo } from 'react';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Translate } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import LoadingState from './common/LoadingState';
import ErrorStateView from './common/ErrorState';
import { buildOpenVisitLink } from './buildOpenVisitLink';
import { daysOpen, isEmptyShell, formatDate } from './openVisitRow';

// SKIN-695 — one row per open (unresolved) visit older than 2 days on the server.
// Mirrors the backend OpenVisitsResponse row shape (opaque identifiers only;
// createdBy is staff, not patient data). Empty shells (no live submission) are
// badged so the clinical partner knows the dossier will offer Void, not Close.
export interface OpenVisitsRow {
    patientInvestigationUuid: string;
    patientIdInvestigation: number;
    visitUuid: string;
    visitStatus: string;
    visitCreatedAt: string;
    createdByUuid?: string | null;
    createdByName?: string | null;
    firstSubmissionId?: number | null;
}

interface OpenVisitsProps {
    loading: boolean;
    error?: Error | null;
    rows: OpenVisitsRow[];
    onRetry?: () => void;
}

const OpenVisits: React.FC<OpenVisitsProps> = ({ loading, error, rows, onRetry }) => {
    const headCells = useMemo(() => ([
        { id: 'patient', alignment: 'left', label: <Translate id="hospital.analytics.clinical.open-visits.patient" /> },
        { id: 'opened', alignment: 'left', label: <Translate id="hospital.analytics.clinical.open-visits.opened" /> },
        { id: 'createdBy', alignment: 'left', label: <Translate id="hospital.analytics.clinical.open-visits.created-by" /> },
        { id: 'action', alignment: 'right', label: <Translate id="general.actions" /> },
    ]), []);

    const tableRows = useMemo(() => rows.map(r => {
        const days = daysOpen(r.visitCreatedAt);
        const empty = isEmptyShell(r.firstSubmissionId);
        return {
            id: r.visitUuid,
            patient: (
                <Stack direction="row" spacing={1} alignItems="center">
                    <span>{r.patientIdInvestigation}</span>
                    {empty && (
                        <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={<Translate id="hospital.analytics.clinical.open-visits.no-data" />}
                        />
                    )}
                </Stack>
            ),
            opened: (
                <span>
                    {formatDate(r.visitCreatedAt)}
                    {days != null && (
                        <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                            <Translate id="hospital.analytics.clinical.open-visits.days-open" data={{ days }} />
                        </Typography>
                    )}
                </span>
            ),
            createdBy: r.createdByName || '-',
            action: (
                <Link component={RouterLink} to={buildOpenVisitLink(r)} underline="hover">
                    <Translate id="hospital.analytics.clinical.open-visits.view-visit" />
                </Link>
            ),
        };
    }), [rows]);

    if (loading) return <LoadingState />;
    if (error) return <ErrorStateView error={error} onRetry={onRetry} />;

    // EnhancedTable infers column types from rows[0], so never render it empty.
    if (!rows.length) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                    <Translate id="hospital.analytics.clinical.open-visits.empty" />
                </Typography>
            </Box>
        );
    }

    return <EnhancedTable noHeader noSelectable disableOrder headCells={headCells} rows={tableRows} />;
};

export default OpenVisits;
